import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LeaseForm, { LeaseFormMode } from "../components/LeaseForm";
import useLeases from "../hooks/useLeases";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { usePropertyQuery } from "../../Properties/pages/PropertyPage/hooks";
import { UserRole } from "@/common/api/datasources/Api";
import { useUsersQuery } from "@/common/api/services/users";
import { useQuery } from "@tanstack/react-query";
import { LeaseFormValues } from "../types/lease";
import { LeaseStatus } from "@/common/api/services/leases";
import { PageHeader } from "@/components/shared/PageHeader";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

const LeaseCreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { createLease } = useLeases();
  const { propertyUUID } = useParams<{ propertyUUID?: string }>();
  const [defaultValues, setDefaultValues] = useState<Partial<LeaseFormValues>>({});

  const isFromProperty = location.pathname.includes('/properties/');

  const handleBackToList = () => {
    navigate(-1);
  };

  const {
    property,
    isLoading: isPropertyLoading
  } = usePropertyQuery(propertyUUID || "");

  const usersQuery = useUsersQuery();
  const {
    data: managersData,
    isLoading: isManagersLoading
  } = useQuery(
    usersQuery.allUsersQuery({
      page: 0,
      size: 50,
      role: UserRole.STAFF,
    }),
  );

  useEffect(() => {
    if (!isPropertyLoading && property) {
      setDefaultValues({
        propertyUUID: propertyUUID || "",
        managerUUID: property.managerUUID || "",
        status: "PENDING" as LeaseStatus,
        paymentDay: 1,
        autoRenew: false,
        monthlyRent: 0,
        securityDeposit: 0,
      });
    }
  }, [isPropertyLoading, property, propertyUUID]);

  const handleSubmit = async (data: LeaseFormValues) => {
    try {
      const formattedData = {
        ...data,
        tenantUUID: !data.tenantUUID || data.tenantUUID === "" ? null : data.tenantUUID,
        managerUUID: !data.managerUUID || data.managerUUID === "" ? null : data.managerUUID,
        startDate: data.startDate ? data.startDate.toISOString().split("T")[0] : "",
        endDate: data.endDate ? data.endDate.toISOString().split("T")[0] : undefined,
      };

      await createLease(formattedData);

      toast({
        title: t("common.success"),
        description: t("leases.create.success"),
      });

      if (isFromProperty && propertyUUID) {
        navigate(`/home/properties/${propertyUUID}/leases`);
      } else {
        navigate("/home/leases");
      }
    } catch (error) {
      console.error("Failed to create lease:", error);

      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("leases.create.error"),
      });
    }
  };

  const isLoading = isPropertyLoading || isManagersLoading;

  return (
    <>
      <LeaseForm
        initialMode={LeaseFormMode.CREATE}
        onSubmit={handleSubmit}
        initialData={defaultValues}
        isLoading={isLoading}
        onBack={handleBackToList}
      />
    </>
  );
};

export default LeaseCreatePage;
